import { useEffect, useState } from "react";
import { getMyAttorneyAccess } from "../api/attorneyAccess";
import {
  getMyInmateAccess,
  requestInmateAccess,
} from "../api/inmateAccess";

import AttorneyNav from "../components/AttorneyNav";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

const blankForm = {
  facility_id: "",
  inmate_number: "",
  last_name: "",
  representation_acknowledged: false,
};

export default function AttorneyInmateAccess() {
  const [facilityAccesses, setFacilityAccesses] = useState([]);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(blankForm);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadData = async () => {
    setLoading(true);

    try {
      const [facilityAccessRes, inmateAccessRes] = await Promise.all([
        getMyAttorneyAccess(),
        getMyInmateAccess(),
      ]);

      setFacilityAccesses(
        (facilityAccessRes.data.data || []).filter(
          (access) => access.status === "approved"
        )
      );

      setRecords(inmateAccessRes.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load client access."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.facility_id ||
      !form.inmate_number ||
      !form.last_name ||
      !form.representation_acknowledged
    ) {
      setError("Complete all fields and confirm representation.");
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      await requestInmateAccess(form);
      setForm(blankForm);
      setMessage("Client access request submitted for facility review.");
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>
          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Attorney Client Access</div>
          </div>
        </div>
        <AttorneyNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="success">{message}</StatusMessage>
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">Client / Inmate Access</h2>
            <p className="text-muted mb-0">
              Request approval to submit privileged legal documents for a specific inmate/client.
            </p>
          </div>

          <div className="stat-card px-4 py-3 text-end">
            <div className="text-muted small">Requests</div>
            <h3 className="mb-0">{records.length}</h3>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-5">
            <div className="portal-card">
              <div className="portal-card-header">
                <h5 className="mb-0">Request Client Access</h5>
                <div className="text-muted small">
                  Inmate rosters are not searchable by attorneys.
                </div>
              </div>

              <div className="portal-card-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="client-facility">Facility</label>
                    <select
                      id="client-facility"
                      className="form-select"
                      value={form.facility_id}
                      onChange={(e) => updateField("facility_id", e.target.value)}
                    >
                      <option value="">Select approved facility</option>
                      {facilityAccesses.map((access) => (
                        <option key={access.id} value={access.facility_id}>
                          {access.facility?.name || "Approved facility"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="client-number">Inmate Number</label>
                    <input
                      id="client-number"
                      className="form-control"
                      value={form.inmate_number}
                      onChange={(e) => updateField("inmate_number", e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="client-last-name">Inmate Last Name</label>
                    <input
                      id="client-last-name"
                      className="form-control"
                      value={form.last_name}
                      onChange={(e) => updateField("last_name", e.target.value)}
                    />
                  </div>

                  <div className="form-check mb-4">
                    <input
                      id="representation_acknowledged"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.representation_acknowledged}
                      onChange={(e) =>
                        updateField("representation_acknowledged", e.target.checked)
                      }
                    />

                    <label
                      className="form-check-label"
                      htmlFor="representation_acknowledged"
                    >
                      I certify that I represent this inmate/client and am requesting access for privileged legal document delivery.
                    </label>
                  </div>

                  <AsyncButton className="btn btn-dark btn-lg w-100" busy={submitting} busyText="Submitting…">
                    Submit Client Access Request
                  </AsyncButton>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="portal-card">
              <div className="portal-card-header d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">My Client Access Requests</h5>
                  <div className="text-muted small">
                    Approved clients become available on the upload page.
                  </div>
                </div>

                <button className="btn btn-sm btn-outline-secondary" onClick={loadData}>
                  Refresh
                </button>
              </div>

              <div className="portal-card-body">
                {loading ? (
                  <p className="mb-0">Loading requests...</p>
                ) : records.length === 0 ? (
                  <div className="text-center py-5">
                    <h5>No client access requests</h5>
                    <p className="text-muted mb-0">
                      Submit a request after facility access is approved.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Facility</th>
                          <th>Submitted Inmate</th>
                          <th>Matched Client</th>
                          <th>Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {records.map((record) => (
                          <tr key={record.id}>
                            <td>{record.facility?.name || "Facility"}</td>

                            <td>
                              #{record.inmate_number_submitted}
                              <div className="text-muted small">
                                Last name: {record.inmate_last_name_submitted}
                              </div>
                            </td>

                            <td>
                              {record.inmate ? (
                                <>
                                  <strong>
                                    {record.inmate.first_name} {record.inmate.last_name}
                                  </strong>
                                  <div className="text-muted small">
                                    Inmate #{record.inmate.inmate_number}
                                  </div>
                                </>
                              ) : (
                                "-"
                              )}
                            </td>

                            <td>
                              <span className={`status-pill status-${record.status}`}>
                                {record.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
